import { getType } from 'typesafe-actions';

import {
  downloadSponsorDashboardReport,
  getSponsorAlertsData,
  getSponsorBenchmarkData,
  getSponsorCommodityComplianceData,
  getSponsorCommodityMetricsData,
  getSponsorCommodityTransactionDetailsData,
  getSponsorComplianceData,
  getSponsorCounterpartyData,
  getSponsorFXComplianceData,
  getSponsorFXMetricsData,
  getSponsorFXTransactionDetailsData,
  getSponsorInstrumentAvailability,
  getSponsorPricingData,
  getSponsorPricingHistoryData,
  getSponsorTopCardsData,
  getSponsorTransactionDetailsData,
} from './actions';
import {
  CommodityComplianceReport,
  CommodityMetrics,
  CommodityTransactionDetails,
  FXComplianceReport,
  FXMetrics,
  FXTransactionDetails,
  InstrumentAvailability,
  SponsorAlertsReport,
  SponsorComplianceReport,
  SponsorDashboardPricing,
  SponsorDashboardPricingHistory,
  SponsorFxExposureReport,
  SponsorPortfolioMetrics,
  SponsorProjectionReport,
  SponsorTransactionDetails,
} from './types';

export interface SponsorDashboardState {
  sponsorTopCardData: {
    data: SponsorPortfolioMetrics | null;
    loading: boolean;
  };
  sponsorBenchmarkData: {
    data: SponsorProjectionReport | null;
    loading: boolean;
  };
  sponsorCounterpartyData: {
    data: SponsorFxExposureReport | null;
    loading: boolean;
  };
  sponsorComplianceData: {
    data: SponsorComplianceReport | null;
    loading: boolean;
  };
  sponsorAlertsData: {
    data: SponsorAlertsReport | null;
    loading: boolean;
  };
  sponsorTransactionDetailsData: {
    data: SponsorTransactionDetails | null;
    loading: boolean;
  };
  downloadSponsorDashboardData: {
    loading: boolean;
  };
  sponsorFXMetricsData: {
    data: FXMetrics | null;
    loading: boolean;
  };
  sponsorFXComplianceData: {
    data: FXComplianceReport | null;
    loading: boolean;
  };
  sponsorFXTransactionDetailsData: {
    data: FXTransactionDetails | null;
    loading: boolean;
  };
  sponsorCommodityMetricsData: {
    data: CommodityMetrics | null;
    loading: boolean;
  };
  sponsorCommodityComplianceData: {
    data: CommodityComplianceReport | null;
    loading: boolean;
  };
  sponsorCommodityTransactionDetailsData: {
    data: CommodityTransactionDetails | null;
    loading: boolean;
  };
  sponsorPricingData: {
    data: SponsorDashboardPricing | null;
    loading: boolean;
  };
  sponsorPricingHistoryData: {
    data: SponsorDashboardPricingHistory | null;
    loading: boolean;
  };
  sponsorInstrumentAvailability: {
    data: InstrumentAvailability | null;
    loading: boolean;
  };
}

const initialState: SponsorDashboardState = {
  sponsorTopCardData: {
    data: null,
    loading: false,
  },
  sponsorBenchmarkData: {
    data: null,
    loading: false,
  },
  sponsorCounterpartyData: {
    data: null,
    loading: false,
  },
  sponsorComplianceData: {
    data: null,
    loading: false,
  },
  sponsorAlertsData: {
    data: null,
    loading: false,
  },
  sponsorTransactionDetailsData: {
    data: null,
    loading: false,
  },
  downloadSponsorDashboardData: {
    loading: false,
  },
  sponsorFXMetricsData: {
    data: null,
    loading: false,
  },
  sponsorFXComplianceData: {
    data: null,
    loading: false,
  },
  sponsorFXTransactionDetailsData: {
    data: null,
    loading: false,
  },
  sponsorCommodityMetricsData: {
    data: null,
    loading: false,
  },
  sponsorCommodityComplianceData: {
    data: null,
    loading: false,
  },
  sponsorCommodityTransactionDetailsData: {
    data: null,
    loading: false,
  },
  sponsorPricingData: {
    data: null,
    loading: false,
  },
  sponsorPricingHistoryData: {
    data: null,
    loading: false,
  },
  sponsorInstrumentAvailability: {
    data: null,
    loading: false,
  },
};

export const sponsorDashboardReducer = (
  state = initialState,
  action: any,
): SponsorDashboardState => {
  switch (action.type) {
    // Sponsor Top Cards Data
    case getType(getSponsorTopCardsData.request):
      return {
        ...state,
        sponsorTopCardData: {
          ...state.sponsorTopCardData,
          loading: true,
        },
      };

    case getType(getSponsorTopCardsData.success):
      return {
        ...state,
        sponsorTopCardData: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(getSponsorTopCardsData.failure):
      return {
        ...state,
        sponsorTopCardData: {
          ...state.sponsorTopCardData,
          loading: false,
        },
      };

    // Sponsor Benchmark Data
    case getType(getSponsorBenchmarkData.request):
      return {
        ...state,
        sponsorBenchmarkData: {
          ...state.sponsorBenchmarkData,
          loading: true,
        },
      };

    case getType(getSponsorBenchmarkData.success):
      return {
        ...state,
        sponsorBenchmarkData: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(getSponsorBenchmarkData.failure):
      return {
        ...state,
        sponsorBenchmarkData: {
          ...state.sponsorBenchmarkData,
          loading: false,
        },
      };

    // Sponsor Counterparty Data
    case getType(getSponsorCounterpartyData.request):
      return {
        ...state,
        sponsorCounterpartyData: {
          ...state.sponsorCounterpartyData,
          loading: true,
        },
      };

    case getType(getSponsorCounterpartyData.success):
      return {
        ...state,
        sponsorCounterpartyData: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(getSponsorCounterpartyData.failure):
      return {
        ...state,
        sponsorCounterpartyData: {
          ...state.sponsorCounterpartyData,
          loading: false,
        },
      };

    // Sponsor Compliance Data
    case getType(getSponsorComplianceData.request):
      return {
        ...state,
        sponsorComplianceData: {
          ...state.sponsorComplianceData,
          loading: true,
        },
      };

    case getType(getSponsorComplianceData.success):
      return {
        ...state,
        sponsorComplianceData: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(getSponsorComplianceData.failure):
      return {
        ...state,
        sponsorComplianceData: {
          ...state.sponsorComplianceData,
          loading: false,
        },
      };

    // Sponsor Alerts Data
    case getType(getSponsorAlertsData.request):
      return {
        ...state,
        sponsorAlertsData: {
          ...state.sponsorAlertsData,
          loading: true,
        },
      };

    case getType(getSponsorAlertsData.success):
      return {
        ...state,
        sponsorAlertsData: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(getSponsorAlertsData.failure):
      return {
        ...state,
        sponsorAlertsData: {
          ...state.sponsorAlertsData,
          loading: false,
        },
      };

    // Sponsor Transaction Details Data
    case getType(getSponsorTransactionDetailsData.request):
      return {
        ...state,
        sponsorTransactionDetailsData: {
          ...state.sponsorTransactionDetailsData,
          loading: true,
        },
      };

    case getType(getSponsorTransactionDetailsData.success):
      return {
        ...state,
        sponsorTransactionDetailsData: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(getSponsorTransactionDetailsData.failure):
      return {
        ...state,
        sponsorTransactionDetailsData: {
          ...state.sponsorTransactionDetailsData,
          loading: false,
        },
      };

    // Download Sponsor Dashboard
    case getType(downloadSponsorDashboardReport.request):
      return {
        ...state,
        downloadSponsorDashboardData: {
          ...state.downloadSponsorDashboardData,
          loading: true,
        },
      };

    case getType(downloadSponsorDashboardReport.success):
      return {
        ...state,
        downloadSponsorDashboardData: {
          ...state.downloadSponsorDashboardData,
          loading: false,
        },
      };

    case getType(downloadSponsorDashboardReport.failure):
      return {
        ...state,
        downloadSponsorDashboardData: {
          ...state.downloadSponsorDashboardData,
          loading: false,
        },
      };

    // FX Metrics
    case getType(getSponsorFXMetricsData.request):
      return { ...state, sponsorFXMetricsData: { ...state.sponsorFXMetricsData, loading: true } };
    case getType(getSponsorFXMetricsData.success):
      return { ...state, sponsorFXMetricsData: { data: action.payload, loading: false } };
    case getType(getSponsorFXMetricsData.failure):
      return { ...state, sponsorFXMetricsData: { ...state.sponsorFXMetricsData, loading: false } };

    // FX Compliance
    case getType(getSponsorFXComplianceData.request):
      return { ...state, sponsorFXComplianceData: { ...state.sponsorFXComplianceData, loading: true } };
    case getType(getSponsorFXComplianceData.success):
      return { ...state, sponsorFXComplianceData: { data: action.payload, loading: false } };
    case getType(getSponsorFXComplianceData.failure):
      return { ...state, sponsorFXComplianceData: { ...state.sponsorFXComplianceData, loading: false } };

    // FX Transaction Details
    case getType(getSponsorFXTransactionDetailsData.request):
      return { ...state, sponsorFXTransactionDetailsData: { ...state.sponsorFXTransactionDetailsData, loading: true } };
    case getType(getSponsorFXTransactionDetailsData.success):
      return { ...state, sponsorFXTransactionDetailsData: { data: action.payload, loading: false } };
    case getType(getSponsorFXTransactionDetailsData.failure):
      return { ...state, sponsorFXTransactionDetailsData: { ...state.sponsorFXTransactionDetailsData, loading: false } };

    // Commodity Metrics
    case getType(getSponsorCommodityMetricsData.request):
      return { ...state, sponsorCommodityMetricsData: { ...state.sponsorCommodityMetricsData, loading: true } };
    case getType(getSponsorCommodityMetricsData.success):
      return { ...state, sponsorCommodityMetricsData: { data: action.payload, loading: false } };
    case getType(getSponsorCommodityMetricsData.failure):
      return { ...state, sponsorCommodityMetricsData: { ...state.sponsorCommodityMetricsData, loading: false } };

    // Commodity Compliance
    case getType(getSponsorCommodityComplianceData.request):
      return { ...state, sponsorCommodityComplianceData: { ...state.sponsorCommodityComplianceData, loading: true } };
    case getType(getSponsorCommodityComplianceData.success):
      return { ...state, sponsorCommodityComplianceData: { data: action.payload, loading: false } };
    case getType(getSponsorCommodityComplianceData.failure):
      return { ...state, sponsorCommodityComplianceData: { ...state.sponsorCommodityComplianceData, loading: false } };

    // Commodity Transaction Details
    case getType(getSponsorCommodityTransactionDetailsData.request):
      return { ...state, sponsorCommodityTransactionDetailsData: { ...state.sponsorCommodityTransactionDetailsData, loading: true } };
    case getType(getSponsorCommodityTransactionDetailsData.success):
      return { ...state, sponsorCommodityTransactionDetailsData: { data: action.payload, loading: false } };
    case getType(getSponsorCommodityTransactionDetailsData.failure):
      return { ...state, sponsorCommodityTransactionDetailsData: { ...state.sponsorCommodityTransactionDetailsData, loading: false } };

    // Sponsor Pricing Data
    case getType(getSponsorPricingData.request):
      return {
        ...state,
        sponsorPricingData: {
          ...state.sponsorPricingData,
          loading: true,
        },
      };

    case getType(getSponsorPricingData.success):
      return {
        ...state,
        sponsorPricingData: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(getSponsorPricingData.failure):
      return {
        ...state,
        sponsorPricingData: {
          ...state.sponsorPricingData,
          loading: false,
        },
      };

    // Sponsor Pricing History Data
    case getType(getSponsorPricingHistoryData.request):
      return {
        ...state,
        sponsorPricingHistoryData: {
          ...state.sponsorPricingHistoryData,
          loading: true,
        },
      };

    case getType(getSponsorPricingHistoryData.success):
      return {
        ...state,
        sponsorPricingHistoryData: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(getSponsorPricingHistoryData.failure):
      return {
        ...state,
        sponsorPricingHistoryData: {
          ...state.sponsorPricingHistoryData,
          loading: false,
        },
      };

    // Instrument Availability
    case getType(getSponsorInstrumentAvailability.request):
      return { ...state, sponsorInstrumentAvailability: { ...state.sponsorInstrumentAvailability, loading: true } };
    case getType(getSponsorInstrumentAvailability.success):
      return { ...state, sponsorInstrumentAvailability: { data: action.payload, loading: false } };
    case getType(getSponsorInstrumentAvailability.failure):
      return { ...state, sponsorInstrumentAvailability: { ...state.sponsorInstrumentAvailability, loading: false } };

    default:
      return state;
  }
};
